import DeleteIcon from '@mui/icons-material/Delete';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Badge,
  Box,
  Button,
  CircularProgress,
  Collapse,
  debounce,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { NATURAL_PERSON_CLIENT_TYPE_ID } from '../constants';
import { useAlert } from '../context/AlertContext';
import { showConfirmDialogComponent } from '../core/ConfirmDialog';
import { LoadingDialog } from '../core/LoadingDialog';
import { NoDataDialog } from '../core/NoDataDialog';
import { LegalClientExport } from './LegalClientExport';
import { CommunicationTab } from '../Communication/CommunicationTab';
import {
  useCheckEmail,
  useCreateLegalClientFullObject,
  useDeleteLegalClient,
  useGetLegalClientContactInformationTypes,
  useGetLegalClientFullObject,
  useGetLegalClientTypes,
  useUpdateLegalClientFullObject,
} from '../hooks/reactQuerys/LegalClientQueries';
import {
  useCreateRelationshipFullObject,
  useDeleteRelationship,
  useGetRelationshipsByLegalClientId,
  useUpdateRelationshipFullObject,
} from '../hooks/reactQuerys/RelationshipQueries';
import {
  ILegalClient,
  ILegalClientFullObject,
  initLegalClient,
  legalClientFullObjectSchema,
  transferLegalClientInput,
} from '../Interfaces/ILegalClient';
import { ILegalClientRelationship } from '../Interfaces/ILegalClientRelationship/ILegalClientRelationship';
import { legalClientRelationshipInputSchema } from '../Interfaces/ILegalClientRelationship/ILegalClientRelationshipInput';
import { ILegalTransaction } from '../Interfaces/ILegalTransaction';
import { AddressTab } from './AddressTab/AddressTab';
import { AddArnotopSyncDialog } from './Arnotop/AddArnotopSyncDialog';
import { BankInformationTab } from './BankInformationTab/BankInformationTab';
import { ContactInformationTab } from './ContactInformationTab/ContactInformationTab';
import { LegalClientTypeForm } from './LegalClientTypeForm';
import { LegalClientUserForm } from './LegalClientUserForm';
import { LegalPersonForm } from './LegalPersonForm';
import { LegalTransactionsTab } from './LegalTransactionsTab/LegalTransactionsTab';
import { LogTab } from './LogTab/LogTab';
import { NaturalPersonForm } from './NaturalPersonForm';
import { RelationshipsTab } from './RelationsTab/RelationshipTab';
import { DocumentTab } from './DocumentTab/DocumentTab';
import { ITaskFullObject } from '../Interfaces/ITask';
import { TaskTable } from '../Task/TaskTable';
import { useGetUserGroups, useGetUsers } from '../hooks/data';
import { useGetLegalClientGroupJoins } from '../hooks/reactQuerys/LegalClientGroups';
import { Business, OpenInNew, Person } from '@mui/icons-material';

interface IProps {
  editedLegalClientId?: number;
  unmount?: (editedLegalClient?: ILegalClientFullObject) => void;
  saveMethod?: SaveMethod;
}

type SaveMethod = 'save' | 'saveAndClose';

export const EditLegalClientDialog = ({
  editedLegalClientId,
  unmount,
  saveMethod = 'save',
}: IProps) => {
  const formRef = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const [formValues, setFormValues] =
    useState<ILegalClientFullObject>(initLegalClient());
  const [savedFormValues, setSavedFormValues] = useState(initLegalClient());
  const [relationships, setRelationships] = useState<
    ILegalClientRelationship[]
  >([]);
  const [savedRelationships, setSavedRelationships] = useState<
    ILegalClientRelationship[]
  >([]);

  const [selectedLegalTransactions, setSelectedLegalTransactions] = useState<
    ILegalTransaction[]
  >([]);

  const [isOpenArnoTopSync, setIsOpenArnoTopSync] = useState(false);
  const [isProcessingUpdate, setIsProcessingUpdate] = useState(false);

  const [currentTab, setCurrentTab] = useState('1');

  const { legalClientId } = useParams();

  const memorizedEditedLegalClientId = useMemo(() => {
    return editedLegalClientId ? editedLegalClientId : Number(legalClientId);
  }, [editedLegalClientId, legalClientId]);

  const { showAlert } = useAlert();

  const { checkEmail, isPendingCheckEmail, abortCheckMail } = useCheckEmail();
  const [sameEmailAdressList, setSameEmailAdressList] = useState<
    ILegalClient[]
  >([]);
  const [isDebouncing, setIsDebouncing] = useState(false);

  const {
    contactInformationTypes,
    isLoadingContactInformationTypes,
    refetchContactInformationTypes,
  } = useGetLegalClientContactInformationTypes();

  const {
    legalClientTypes,
    isLoadingLegalClientTypes,
    refetchLegalClientTypes,
  } = useGetLegalClientTypes();

  const {
    createRelationshipFullObject,
    isPendingCreateRelationshipFullObject,
  } = useCreateRelationshipFullObject();

  const { createLegalClientFullObject, isPendingCreateLegalClientFullObject } =
    useCreateLegalClientFullObject();

  const { deleteLegalClient, isPendingDeleteLegalClient } =
    useDeleteLegalClient();

  const { groupJoin, isLoadingGroupJoin, refetchGroupJoin } =
    useGetLegalClientGroupJoins();

  const {
    legalClientRelationships,
    isLoadingLegalClientRelationships,
    refetchLegalClientRelationships,
  } = useGetRelationshipsByLegalClientId(memorizedEditedLegalClientId);

  const {
    legalClientFullObject,
    isFetchingLegalClientFullObject,
    refetchLegalClientFullObject,
  } = useGetLegalClientFullObject(memorizedEditedLegalClientId);

  const { updateLegalClientFullObject, isPendingUpdateLegalClientFullObject } =
    useUpdateLegalClientFullObject();

  const {
    updateRelationshipFullObject,
    isPendingUpdateRelationshipFullObject,
  } = useUpdateRelationshipFullObject();

  const { deleteRelationship, isPendingDeleteRelationship } =
    useDeleteRelationship();

  const { users, isLoadingUsers } = useGetUsers();
  const { userGroups, isLoadingUserGroups } = useGetUserGroups();

  useEffect(() => {
    if (
      !legalClientRelationships ||
      isEqual(legalClientRelationships, relationships)
    )
      return;
    setRelationships(legalClientRelationships);
    setSavedRelationships(legalClientRelationships);
  }, [legalClientRelationships]);

  useEffect(() => {
    if (!legalClientFullObject || isEqual(formValues, legalClientFullObject))
      return;
    setFormValues(legalClientFullObject!);
    setSavedFormValues(legalClientFullObject!);
    setSelectedLegalTransactions(
      legalClientFullObject.LegalTransactionArray.map(lt => lt.LegalTransaction)
    );
  }, [legalClientFullObject]);

  {
    /*
  Weiß nicht ob man das noch braucht, war für Fehlerbehebung
  useEffect(() => {
    if (!legalClientFullObject || !legalClientFullObject.LegalClientGroups) return;

    setFormValues(prev => ({
      ...prev,
      LegalClientGroups: legalClientFullObject.LegalClientGroups
    }));
  }, [legalClientFullObject?.LegalClientGroups]);
  */
  }

  useEffect(() => {
    setFormValues(prev => {
      const prevArray = prev.LegalTransactionArray ?? [];

      const prevMap = new Map(
        prevArray.map(x => [x.LegalTransaction.idLegalTransaction, x])
      );

      return {
        ...prev,
        LegalTransactionArray: (selectedLegalTransactions ?? []).map(lt => {
          const existing = prevMap.get(lt.idLegalTransaction);

          const existingRoleId =
            existing?.idLegalRole ??
            (existing?.LegalRole as any)?.idLegalRole ??
            null;

          return {
            LegalTransaction: lt,
            idLegalRole: existingRoleId,
            LegalRole: null,
          };
        }),
      };
    });
  }, [selectedLegalTransactions]);

  const resetForm = () => {
    setFormValues(legalClientFullObject!);
    setSavedFormValues(legalClientFullObject!);
    setRelationships(legalClientRelationships!);
    setSavedRelationships(legalClientRelationships!);
  };

  const onCheckEmail = async (email: string) => {
    const checkList = await checkEmail({ Email: email });
    setSameEmailAdressList(checkList);
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((testString: string) => {
        if (testString !== '') {
          abortCheckMail();
          onCheckEmail(testString);
        } else {
          setSameEmailAdressList([]);
        }
        setFormValues((prev: ILegalClientFullObject) => ({
          ...prev,
          Email: testString,

          ...(!testString || prev.idLegalClientType !== 1
            ? {
                passwordResetRequired: false,
                resetPassword: false,
                isReceivingEmailEnabled: false,
                loginPossible: false,
              }
            : (formValues.idLegalClient < 0 ||
                (legalClientFullObject !== undefined &&
                  (legalClientFullObject.Email === undefined ||
                    legalClientFullObject.Email === null ||
                    legalClientFullObject.Email === ''))) && {
                passwordResetRequired: true,
                resetPassword: false,
                isReceivingEmailEnabled: false,
                loginPossible: true,
              }),
        }));

        setIsDebouncing(false);
      }, 700),
    []
  );

  const onUpdate = async (legalClient: ILegalClientFullObject = formValues) => {
    try {
      setIsProcessingUpdate(true);
      const input: ILegalClientFullObject =
        transferLegalClientInput(legalClient);

      const { success, data, error } =
        legalClientFullObjectSchema.safeParse(input);

      if (
        !success ||
        (formValues.idLegalClientType === 1 && !formValues.FirstName) // Manuel validation of FirstName because it is not required in the schema but should be required in the form
      ) {
        console.error(error);
        showAlert({
          text: 'Bitte füllen Sie alle Felder aus',
          severity: 'error',
        });
        setIsProcessingUpdate(false);
        return;
      }

      if (isMainFormEdited) {
        let updatedLegalClient;
        if (legalClient.idLegalClient < 0) {
          updatedLegalClient = await createLegalClientFullObject(data);
        } else {
          updatedLegalClient = await updateLegalClientFullObject(data);
        }
        setSavedFormValues(updatedLegalClient);
        setFormValues(updatedLegalClient);

        //await refetchLegalClients();
        await refetchGroupJoin();
      }

      if (isRelationshipsFormEdited) {
        await handleRelationships();
        await refetchAll();
      }

      showAlert({ text: 'Mandant erfolgreich bearbeitet' });
      setIsProcessingUpdate(false);
      if (saveMethod === 'saveAndClose') closeMethod(formValues);
    } catch (error) {
      await refetchAll();

      showAlert({
        text: 'Fehler beim Speichern des Mandanten',
        severity: 'error',
      });
      resetForm();

      setIsProcessingUpdate(false);
    }
  };

  const handleRelationships = async () => {
    let tasks: Promise<void>[] = [];

    // If no existing relationships, create all from `relationships`
    if (!legalClientRelationships) {
      tasks = relationships.map(r =>
        createRelationshipFullObject(
          legalClientRelationshipInputSchema.parse(r)
        )
      );
    } else {
      // Create a map for quick lookups of existing relationships
      const existingMap = new Map(
        legalClientRelationships.map(rel => [rel.idRelationship, rel])
      );

      for (const r of relationships) {
        if (r.idRelationship < 0) {
          // Create new relationship
          tasks.push(
            createRelationshipFullObject(
              legalClientRelationshipInputSchema.parse(r)
            )
          );
        } else {
          const existing = existingMap.get(r.idRelationship);
          if (existing) {
            if (!isEqual(existing, r)) {
              // Update only if there's a change
              tasks.push(
                updateRelationshipFullObject(
                  legalClientRelationshipInputSchema.parse(r)
                )
              );
            }
            existingMap.delete(r.idRelationship);
          }
        }
      }

      tasks.push(
        ...Array.from(existingMap.values()).map(({ idRelationship }) =>
          deleteRelationship(idRelationship)
        )
      );
    }
    await Promise.all(tasks);
  };

  const onSubmit = () => {
    if (formRef.current && formRef.current.checkValidity()) {
      onUpdate();
    } else if (formRef.current && !formRef.current.checkValidity()) {
      const elements = Array.from(formRef.current.elements).reverse();

      elements.forEach((element: any) => {
        if (element.reportValidity()) {
          element.reportValidity();
        }
      });
    }
  };

  const closeMethod = (editedLegalClient?: ILegalClientFullObject) =>
    unmount ? unmount(editedLegalClient) : navigate('/clients');

  const handleConfirmedClose = async () => {
    if (isProcessingUpdate) return;
    if (isEdited) {
      const isConfirm = await showConfirmDialogComponent(
        'Warnung',
        'Sind Sie sicher, dass Sie den Mandanten ohne Speichern schließen wollen?',
        'Schließen',
        'Abbrechen',
        theme
      );
      if (isConfirm) closeMethod();
    } else {
      closeMethod();
    }
  };

  const refetchAll = () => {
    refetchContactInformationTypes();
    //refetchLegalClientTypes();
    refetchLegalClientRelationships();
    refetchLegalClientFullObject();
  };

  const isDuplicateEmail =
    sameEmailAdressList.filter(
      x => x.idLegalClient !== formValues.idLegalClient
    ).length > 0;

  const isMainFormEdited = !isEqual(
    {
      ...formValues,
      LegalClientGroups: formValues.LegalClientGroups?.map(
        g => g.idLegalClientGroup
      ).sort(),
    },
    {
      ...savedFormValues,
      LegalClientGroups: savedFormValues.LegalClientGroups?.map(
        g => g.idLegalClientGroup
      ).sort(),
    }
  );

  const isRelationshipsFormEdited = !isEqual(relationships, savedRelationships);

  const isEdited = isMainFormEdited || isRelationshipsFormEdited;

  const onDelete = useCallback(async () => {
    const isConfirm = await showConfirmDialogComponent(
      'Warnung',
      'Sind Sie sicher, dass Sie diesen Mandanten löschen wollen?',
      'Löschen',
      'Schließen',
      theme
    );
    if (!isConfirm) return;
    await deleteLegalClient(formValues.idLegalClient);
    closeMethod();
  }, [formValues.idLegalClient]);

  // Alles um die Tabs scrollbar zu machen

  const tabListRef = useRef<HTMLDivElement | null>(null);

  const centerActiveTab = useCallback(() => {
    const list = tabListRef.current;
    if (!list) return;

    const scroller = list.querySelector<HTMLElement>('.MuiTabs-scroller');
    const activeTab = list.querySelector<HTMLElement>(
      '[role="tab"][aria-selected="true"]'
    );
    if (!scroller || !activeTab) return;

    const target =
      activeTab.offsetLeft +
      activeTab.offsetWidth / 2 -
      scroller.clientWidth / 2;

    scroller.scrollTo({
      left: Math.max(0, target),
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    const id = requestAnimationFrame(centerActiveTab);
    return () => cancelAnimationFrame(id);
  }, [currentTab, centerActiveTab]);

  //Bei Fenserrezise dat ding updaten dass es in der Mitte bleibt
  useEffect(() => {
    const onResize = () => centerActiveTab();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [centerActiveTab]);

  // This is needed to track first and last name changes in the relationships tab
  const liveUpdatedRelationships = useMemo(
    () =>
      relationships.map(r => ({
        ...r,
        Members: r.Members.map(m =>
          m.idLegalClient === memorizedEditedLegalClientId
            ? {
                ...m,
                LegalClient: {
                  ...m.LegalClient,
                  FirstName: formValues.FirstName,
                  LastName: formValues.LastName,
                },
              }
            : m
        ),
      })),
    [relationships, formValues.FirstName, formValues.LastName]
  );

  const isPending =
    isPendingUpdateLegalClientFullObject ||
    isPendingUpdateRelationshipFullObject ||
    isPendingDeleteRelationship ||
    isPendingCreateRelationshipFullObject ||
    isPendingCreateLegalClientFullObject;

  if (
    isLoadingContactInformationTypes ||
    isLoadingLegalClientTypes ||
    isLoadingLegalClientRelationships ||
    isFetchingLegalClientFullObject ||
    isPendingDeleteLegalClient ||
    isLoadingUsers ||
    isLoadingUserGroups ||
    formValues.idLegalClient <= 0
  )
    return <LoadingDialog unmount={closeMethod} />;

  if (
    legalClientTypes === undefined ||
    (editedLegalClientId !== undefined &&
      editedLegalClientId > 0 &&
      (contactInformationTypes === undefined ||
        legalClientRelationships === undefined ||
        legalClientFullObject === undefined)) ||
    !users ||
    !userGroups
  )
    return <NoDataDialog unmount={closeMethod} refetch={refetchAll} />;

  return (
    <>
      <Dialog
        open
        onClose={handleConfirmedClose}
        maxWidth='lg'
        fullWidth
        scroll='paper'
      >
        {isPending && (
          <LinearProgress
            sx={{
              height: '4px',
            }}
          />
        )}
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 3,
            py: 2,
          }}
        >
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            width='100%'
          >
            <Typography variant='h5'>
              {formValues?.FirstName} {formValues?.LastName}
            </Typography>
            <Box display='flex' alignItems='center' gap={1}>
              {legalClientFullObject?.idLegalClient &&
                legalClientFullObject.idLegalClient > 0 && (
                  <LegalClientExport
                    idLegalClient={legalClientFullObject.idLegalClient}
                  />
                )}
              <IconButton onClick={onDelete}>
                <DeleteIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>

        <DialogContent sx={{ minHeight: '70vh' }}>
          <Collapse in={isDuplicateEmail}>
            <Alert severity='warning' sx={{ mb: 3 }}>
              <AlertTitle>Warnung: E-Mail-Adresse</AlertTitle>
              Diese E-Mail-Adresse ist bereits bei folgenden Mandanten
              hinterlegt:
              <Stack spacing={1.5} sx={{ mt: 2 }}>
                {sameEmailAdressList
                  .filter(lc => lc.idLegalClient !== formValues.idLegalClient)
                  .map(lc => (
                    <Paper
                      component='a'
                      href={`/clients/${lc.idLegalClient}`}
                      target='_blank'
                      elevation={0}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        textDecoration: 'none',
                        color: 'inherit',
                        transition: 'all .15s',
                        '&:hover': {
                          borderColor: 'warning.main',
                          backgroundColor: 'action.hover',
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      {lc.idBusinessForm !== 1 ? <Business /> : <Person />}

                      <Box sx={{ flexGrow: 1 }}>
                        {lc.idBusinessForm === 1 && lc.FirstName} {lc.LastName}
                      </Box>

                      <OpenInNew fontSize='small' color='action' />
                    </Paper>
                  ))}
              </Stack>
            </Alert>
          </Collapse>

          <Grid container spacing={2}>
            <form ref={formRef}>
              <Grid container item spacing={2} pt={1}>
                <Grid item xs={3}>
                  <LegalClientTypeForm
                    formValues={formValues}
                    setFormValues={setFormValues}
                  />
                </Grid>

                {formValues.idLegalClientType ===
                NATURAL_PERSON_CLIENT_TYPE_ID ? (
                  <NaturalPersonForm
                    orgObject={legalClientFullObject}
                    formValues={formValues}
                    setFormValues={setFormValues}
                  />
                ) : (
                  <LegalPersonForm
                    formValues={formValues}
                    setFormValues={setFormValues}
                  />
                )}
                <Grid item xs={12}>
                  <Box sx={{ mt: 0.5 }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label='E-Mail-Adresse'
                    size='small'
                    defaultValue={formValues.Email || ''}
                    fullWidth
                    type='email'
                    onChange={({
                      target,
                    }: React.ChangeEvent<HTMLInputElement>) => {
                      setIsDebouncing(true);
                      debouncedSearch(target.value);
                    }}
                  />
                </Grid>
              </Grid>
            </form>

            <Grid item xs={12}>
              <LegalClientUserForm
                formValues={formValues}
                setFormValues={setFormValues}
              />
            </Grid>

            {/*
            <Grid item xs={12}>
              <ArnoTopSyncForm
                legalClient={formValues}
                onCheckedChange={checked => {
                  if (checked) {
                    setFormValues({
                      ...formValues,
                      syncWithArnoTop: true,
                      syncType: 1,
                    });
                    setIsOpenArnoTopSync(true);
                  } else {
                    setFormValues({
                      ...formValues,
                      syncWithArnoTop: false,
                      syncType: undefined,
                      idArnoTop: undefined,
                    });
                  }
                }}
              />
            </Grid>
             */}
          </Grid>

          <TabContext value={currentTab}>
            <Box
              sx={{
                display: 'flex',
                mt: 4,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Box
                sx={{
                  borderRight: 1,
                  borderColor: 'divider',
                  minWidth: 220,
                  flexShrink: 0,
                }}
              >
                <TabList
                  ref={tabListRef}
                  orientation='vertical'
                  onChange={(_, newValue) => setCurrentTab(newValue)}
                  variant='scrollable'
                  scrollButtons='auto'
                  allowScrollButtonsMobile
                  aria-label='Mandanten-Tabs'
                  sx={{
                    '& .MuiTab-root': {
                      alignItems: 'flex-start',
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      minHeight: 44,
                    },
                  }}
                >
                  <Tab label='Addressen' value='1' />
                  <Tab label='Kontakt' value='2' />
                  <Tab label='Kommunikation' value='11' />
                  <Tab label='Rechtsgeschäfte' value='3' />
                  <Tab
                    label={
                      <Badge
                        badgeContent={
                          formValues.Tasks.filter(
                            x => x.Closed_at === null
                          ).filter(x => new Date(x.Deadline_at) <= new Date())
                            .length
                        }
                        color='primary'
                      >
                        Aufgaben
                      </Badge>
                    }
                    value='4'
                  />
                  <Tab label='Beziehungen' value='5' />
                  <Tab label='Bankinformationen' value='6' />
                  <Tooltip title='Noch nicht verfügbar'>
                    <Box>
                      <Tab label='Gesprächsprotokolle' value='7' disabled />
                    </Box>
                  </Tooltip>
                  <Tab label='Dokumente' value='8' />
                  <Tab label='Log' value='9' />
                  <Tab label='Vermögen' value='10' />
                </TabList>
              </Box>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <TabPanel value='1'>
                  <AddressTab
                    formValues={formValues}
                    setFormValues={setFormValues}
                  />
                </TabPanel>
                <TabPanel value='2'>
                  <ContactInformationTab
                    contactInformationTypes={contactInformationTypes!}
                    formValues={formValues}
                    setFormValues={setFormValues}
                  />
                </TabPanel>
                <TabPanel value='3'>
                  <LegalTransactionsTab
                    legalTransactions={selectedLegalTransactions}
                    setLegalTransactions={setSelectedLegalTransactions}
                  />
                </TabPanel>
                <TabPanel value='4'>
                  <TaskTable
                    tasks={formValues.Tasks}
                    setTasks={(tasks: ITaskFullObject[]) =>
                      setFormValues(prev => ({
                        ...prev,
                        Tasks: tasks,
                      }))
                    }
                    users={users}
                    userGroups={userGroups}
                    legalClients={[formValues]}
                    legalClient={formValues}
                  />
                </TabPanel>
                <TabPanel value='5'>
                  <RelationshipsTab
                    relationships={liveUpdatedRelationships}
                    setRelationships={setRelationships}
                  />
                </TabPanel>
                <TabPanel value='6'>
                  <BankInformationTab
                    formValues={formValues}
                    setFormValues={setFormValues}
                  />
                </TabPanel>
                <TabPanel value='8'>
                  <DocumentTab
                    legalClientId={memorizedEditedLegalClientId}
                    relationships={liveUpdatedRelationships}
                    legalTransactions={selectedLegalTransactions}
                    formValues={formValues}
                    setFormValues={setFormValues}
                  />
                </TabPanel>

                <TabPanel value='9'>
                  <LogTab idLegalClient={memorizedEditedLegalClientId} />
                </TabPanel>
                <TabPanel value='10'>
                  {/*<AssetsTab assetIds={legalClientFullObject?.AssetArray!} />*/}
                </TabPanel>
                <TabPanel value='11'>
                  <CommunicationTab
                    idLegalClient={legalClientFullObject?.idLegalClient ?? null}
                  />
                </TabPanel>
              </Box>
            </Box>
          </TabContext>
        </DialogContent>
        <DialogActions
          sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}
        >
          <Button variant='outlined' onClick={handleConfirmedClose}>
            Schließen
          </Button>
          <Button
            variant='contained'
            disabled={
              isPendingUpdateLegalClientFullObject ||
              isPendingCheckEmail ||
              isDebouncing ||
              isDuplicateEmail ||
              isProcessingUpdate
            }
            onClick={onSubmit}
          >
            {isPending || isProcessingUpdate ? (
              <Box display='flex' alignItems='center' gap={1}>
                <span>Speichern</span>
                <CircularProgress size={20} />
              </Box>
            ) : (
              'Speichern'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {isOpenArnoTopSync && (
        <AddArnotopSyncDialog
          legalClient={formValues}
          onSave={arnotopSyncState => {
            setFormValues({
              ...formValues,
              syncWithArnoTop: arnotopSyncState.syncWithArnoTop,
              syncType: arnotopSyncState.syncType,
              idArnoTop: arnotopSyncState.idArnoTop,
            });
            setIsOpenArnoTopSync(false);
          }}
        />
      )}
    </>
  );
};
